// EPI Service Request 的备注列表+新增弹窗
// 由 SectionFunction/ServiceRequest.js 的 showServiceRequestNote 动态挂载渲染
// 顶部 模版下拉 + TextArea 输入 + Add/Close 按钮；下方 Table 渲染历史备注
// Add 成功后不关闭弹窗，仅刷新表格
// 备注默认对 Client Portal 可见（ClientPortal 恒为 true），不再提供 Public Note 复选框
import React from 'react';
import { Input, Button, Space, Table, Divider, Popconfirm, Tooltip, message, Select } from 'antd';
import AntDraggableModal from 'components/AntDraggableModal';
import CustomBadge from 'components/CustomBadge';
import { httpClient } from 'util/Api';
import { config } from 'util/config';

export default class ServiceRequestNoteModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: true,                  // 自身控制 open（关闭后调 props.onClose 卸载）
      notes: [],
      loading: false,
      content: '',
      submitting: false,
      templateOptions: [],
      templateId: 0,   // 默认选中 "- Select Template -"（value 0）
    };
  }

  componentDidMount() {
    this.loadNotes();
    this.loadTemplates();
  }

  // 加载本公司的 Service Request 备注模版下拉选项
  loadTemplates = () => {
    let that = this;
    httpClient.get('/Admin/ServiceRequestNoteTemplate/GetSelectOptionList')
      .then(({ data }) => {
        if (data.result) {
          that.setState({ templateOptions: data.data || [] });
        }
      })
      .catch(() => { /* 模版加载失败不阻塞备注功能，静默处理 */ });
  };

  // 选择模版后把模版内容填充到备注输入框；选 "- Select Template -"(0) 不覆盖已输入内容（参考 EmailTemplate）
  onSelectTemplate = (templateId) => {
    if (!templateId) {
      this.setState({ templateId: 0 });
      return;
    }
    const option = this.state.templateOptions.find((o) => o.Value === templateId);
    this.setState({
      templateId,
      content: option ? (option.Data || '') : this.state.content,
    });
  };

  loadNotes = () => {
    let that = this;
    this.setState({ loading: true });
    httpClient.get('/Business/ServiceRequest/GetNoteList?serviceRequestId=' + this.props.serviceRequestId)
      .then(({ data }) => {
        if (data.result) {
          that.setState({ notes: data.data || [], loading: false });
        } else {
          message.error(data.message || 'Failed to load notes.');
          that.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error('Failed to load notes.');
        that.setState({ loading: false });
      });
  };

  onAdd = () => {
    let text = (this.state.content || '').trim();
    if (text === '') {
      message.warning('Please type a note.');
      return;
    }
    let that = this;
    this.setState({ submitting: true });
    httpClient.post('/Business/ServiceRequest/AddNote', {
      ServiceRequestId: this.props.serviceRequestId,
      Content: text,
      ClientPortal: true,   // 备注默认对 Client Portal 可见
    })
      .then(({ data }) => {
        if (data.result) {
          // 添加后不关闭弹窗：清输入（含模版选择）+ 刷新表格
          that.setState({ content: '', templateId: 0, submitting: false }, that.loadNotes);
          // 通知外层 Service Request 表格刷新（更新 Notes 列最新备注）
          that.props.onChanged && that.props.onChanged();
        } else {
          message.error(data.message || 'Failed to add note.');
          that.setState({ submitting: false });
        }
      })
      .catch(() => {
        message.error('Failed to add note.');
        that.setState({ submitting: false });
      });
  };

  onDelete = (record) => {
    let that = this;
    httpClient.delete('/Business/ServiceRequest/DeleteNote/' + (record.key || record.ServiceRequestNoteId))
      .then(({ data }) => {
        if (data.result) {
          that.loadNotes();
          // 删除备注后同步刷新外层表格的 Notes 列（最新备注可能变化）
          that.props.onChanged && that.props.onChanged();
        } else {
          message.error(data.message || 'Failed to delete note.');
        }
      })
      .catch(() => message.error('Failed to delete note.'));
  };

  close = () => {
    this.setState({ open: false });
    // 等 Modal 关闭动画走完再卸载组件
    setTimeout(() => { this.props.onClose && this.props.onClose(); }, 200);
  };

  render() {
    const columns = [
      // 备注内容保留回车换行显示
      { title: 'Note', dataIndex: 'Content', ellipsis: false, render: (v) => <div style={{ whiteSpace: 'pre-wrap' }}>{v}</div> },
      { title: 'By', dataIndex: 'CreatedBy', width: 200 },
      {
        title: 'Date', dataIndex: 'DateCreated', width: 160,
      },
      // {
      //   title: 'Public', dataIndex: 'ClientPortal', width: 80,
      //   render: (v) => v
      //     ? <CustomBadge text="Yes" backgroundColor="#28A745" cursor="default" />
      //     : <CustomBadge text="No" backgroundColor="#6C757D" cursor="default" />,
      // },
      {
        title: '', key: 'action', width: 50, align: 'center',
        render: (_, record) => (
          <Popconfirm
            placement="bottom"
            title={config.component.defaultDeleteConfirmText}
            onConfirm={() => this.onDelete(record)}
          >
            <Tooltip title="Delete">
              <Button
                icon={<img className="svg-img-btn" src={'assets/images/icon/delete.svg'} alt="" />}
                type="link"
              />
            </Tooltip>
          </Popconfirm>
        ),
      },
    ];

    return (
      <AntDraggableModal
        title="Notes"
        open={this.state.open}
        onCancel={this.close}
        width={1440}
        maskClosable={config.component.Modal.maskClosable}
        footer={null}
      >
        {/* 备注模版下拉：默认 "- Select Template -"（参考 EmailTemplate） */}
        <Select
          style={{ width: '100%', marginBottom: 12 }}
          showSearch
          value={this.state.templateId}
          onChange={this.onSelectTemplate}
          getPopupContainer={(trigger) => trigger.parentNode}
          filterOption={(inputValue, option) => option?.children?.toLowerCase().includes(inputValue.toLowerCase())}
        >
          <Select.Option value={0}>- Select Template -</Select.Option>
          {this.state.templateOptions.map((o) => (
            <Select.Option key={o.Value} value={o.Value}>{o.Key}</Select.Option>
          ))}
        </Select>

        {/* 顶部：TextArea 输入（高度 200px） */}
        <Input.TextArea
          value={this.state.content}
          onChange={(e) => this.setState({ content: e.target.value })}
          placeholder="Type a note..."
          maxLength={2000}
          autoSize={false}
          style={{ height: 200 }}
        />

        {/* Add / Close 按钮（同一行，右对齐）；备注默认对 Client Portal 可见，无需复选框 */}
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Space size="small">
            <Button
              type="primary"
              loading={this.state.submitting}
              onClick={this.onAdd}
              size={config.component.size}
            >
              Add
            </Button>
            <Button onClick={this.close} size={config.component.size}>
              Close
            </Button>
          </Space>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* 历史备注 Table */}
        <Table
          bordered
          size="small"
          rowKey={(r) => r.key || r.ServiceRequestNoteId}
          columns={columns}
          dataSource={this.state.notes}
          loading={this.state.loading}
          pagination={false}
          scroll={config.component.Table.scroll}
        />
      </AntDraggableModal>
    );
  }
}
